// Cell-based spatial hash. O(n) neighborhood queries by bucketing entities
// into uniform grid cells, then iterating only the relevant cells.
//
// Use:
//   const h = new SpatialHash(80);
//   h.clear();
//   for every entity: h.insert(id, x, y);
//   h.forEachInRadius(cx, cy, r, (id) => { ... });

export class SpatialHash {
  private readonly cells = new Map<number, number[]>();
  /** Cell size in world units. Pick close to typical query radius. */
  constructor(public readonly cellSize: number = 80) {}

  clear(): void {
    this.cells.clear();
  }

  private key(cx: number, cy: number): number {
    // Pack into a single number. Range ±32k cells is plenty for v1.
    // eslint-disable-next-line no-bitwise
    return ((cx & 0xffff) << 16) | (cy & 0xffff);
  }

  insert(id: number, x: number, y: number): void {
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    const k = this.key(cx, cy);
    let bucket = this.cells.get(k);
    if (!bucket) {
      bucket = [];
      this.cells.set(k, bucket);
    }
    bucket.push(id);
  }

  /** Visit every entity whose cell overlaps the disc (cx, cy, r). May report
   *  some entities outside the radius — callers should filter by distance. */
  forEachInRadius(cx: number, cy: number, r: number, fn: (id: number) => void): void {
    const minCx = Math.floor((cx - r) / this.cellSize);
    const maxCx = Math.floor((cx + r) / this.cellSize);
    const minCy = Math.floor((cy - r) / this.cellSize);
    const maxCy = Math.floor((cy + r) / this.cellSize);
    for (let x = minCx; x <= maxCx; x++) {
      for (let y = minCy; y <= maxCy; y++) {
        const bucket = this.cells.get(this.key(x, y));
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i++) fn(bucket[i]!);
      }
    }
  }
}
